import type { System } from '@/core/system';
import type { Engine } from '@/core/engine';
import type { PlayerTelemetry } from '@/core/types';

/**
 * Records the ground track of everywhere the player has walked, so the finale can
 * ink that whole route across the city floor — the literal drawing of a life spent
 * in this place. It listens to the same per-frame telemetry the analyzer consumes
 * and samples a point only once the player has moved a stride from the last one, so
 * the buffer stays small and evenly spaced regardless of framerate or dawdling.
 *
 * Storage is a flat [x0,z0,x1,z1,…] array kept in chronological order. It is ring-
 * capped: once full, the oldest points are dropped so a marathon session can't grow
 * the buffer without bound while still preserving stroke order for drawing.
 */
const MIN_SPACING = 1.5; // metres between recorded points
const MIN_SPACING_SQ = MIN_SPACING * MIN_SPACING;
const MAX_POINTS = 2600; // ring cap (each point is an [x,z] pair)

export class PathRecorder implements System {
  readonly name = 'path';

  /** Flat pairs: [x0, z0, x1, z1, …], oldest → newest. */
  private pts: number[] = [];
  private lastX = 0;
  private lastZ = 0;
  private has = false;

  constructor(private engine: Engine) {}

  init(): void {
    this.engine.events.on('player:telemetry', (t) => this.sample(t));
  }

  private sample(t: PlayerTelemetry): void {
    const x = t.position.x;
    const z = t.position.z;
    if (this.has) {
      const dx = x - this.lastX;
      const dz = z - this.lastZ;
      if (dx * dx + dz * dz < MIN_SPACING_SQ) return;
    }
    this.pts.push(x, z);
    this.lastX = x;
    this.lastZ = z;
    this.has = true;
    // Ring cap: drop oldest pairs from the front once over capacity. Rare (only
    // every ≥1.5m of travel), so the occasional block-shift is negligible.
    const overflow = this.pts.length - MAX_POINTS * 2;
    if (overflow > 0) this.pts.splice(0, overflow);
  }

  /** The recorded route as a flat [x0,z0,…] array, oldest → newest (a copy). */
  points(): number[] {
    return this.pts.slice();
  }

  /** Round to 0.1m to keep saves compact; order preserved. */
  serialize(): number[] {
    return this.pts.map((v) => Math.round(v * 10) / 10);
  }

  restore(points: number[] | undefined): void {
    if (!points || points.length < 2) {
      this.pts = [];
      this.has = false;
      return;
    }
    // Guard against an odd-length payload by dropping any trailing stray.
    const n = points.length - (points.length % 2);
    this.pts = points.slice(0, n);
    this.lastX = this.pts[this.pts.length - 2];
    this.lastZ = this.pts[this.pts.length - 1];
    this.has = true;
  }
}
